import React, { useState, useEffect, useCallback } from 'react';

// ── Color palette (KeywordIP) ────────────────────────────────────────────────
const C = {
  text: '#0f172a',
  muted: '#64748b',
  faint: '#94a3b8',
  border: '#e5e7eb',
  bg: '#f8fafc',
  good: '#10b981',
  bad: '#ef4444',
  brand: '#0e304c',
  accent: '#bb9274',
};

const BUCKETS = [
  { key: 'top3',   label: 'Top 3',   color: C.good,    desc: 'Positions 1\u20133' },
  { key: 'page1',  label: 'Page 1',  color: '#3b82f6', desc: 'Positions 4\u201310' },
  { key: 'page2',  label: 'Page 2',  color: '#f59e0b', desc: 'Positions 11\u201320' },
  { key: 'page3',  label: 'Page 3',  color: '#f97316', desc: 'Positions 21\u201350' },
  { key: 'beyond', label: 'Beyond',  color: C.faint,   desc: 'Position 51+' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtMonth(iso) {
  const d = new Date(iso);
  return `${MONTH_NAMES[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
}

// ── Hero summary: first vs latest ────────────────────────────────────────────
function HistoryHero({ months }) {
  if (!months || months.length < 2) {
    return (
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.muted, textAlign: 'center' }}>
          Not enough historical data to compare. {months?.length === 1 ? 'Need at least 2 months.' : ''}
        </div>
      </div>
    );
  }
  const first = months[0];
  const latest = months[months.length - 1];
  const monthsSpan = months.length;

  const trend = (cur, prev) => {
    if (prev === 0 && cur === 0) return { txt: '\u2014', color: C.faint };
    if (prev === 0) return { txt: `+${cur}`, color: C.good };
    const delta = cur - prev;
    const pct = Math.round((delta / prev) * 100);
    if (delta === 0) return { txt: 'no change', color: C.faint };
    return { txt: `${delta > 0 ? '+' : ''}${delta} (${delta > 0 ? '+' : ''}${pct}%)`, color: delta > 0 ? C.good : C.bad };
  };

  const top3Trend = trend(latest.top3, first.top3);
  const page1Trend = trend(latest.page1 + latest.top3, first.page1 + first.top3);
  const totalTrend = trend(latest.total, first.total);

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 }}>
        {fmtMonth(first.month)} → {fmtMonth(latest.month)} · {monthsSpan} months
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Top 3 keywords</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {latest.top3}
          </div>
          <div style={{ fontSize: 12, color: top3Trend.color, marginTop: 4, fontWeight: 600 }}>
            {top3Trend.txt} from {first.top3}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Page 1 keywords</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {latest.top3 + latest.page1}
          </div>
          <div style={{ fontSize: 12, color: page1Trend.color, marginTop: 4, fontWeight: 600 }}>
            {page1Trend.txt} from {first.top3 + first.page1}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>Total keywords ranking</div>
          <div style={{ fontSize: 32, fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: '-0.02em' }}>
            {latest.total}
          </div>
          <div style={{ fontSize: 12, color: totalTrend.color, marginTop: 4, fontWeight: 600 }}>
            {totalTrend.txt} from {first.total}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Stacked bar chart (pure SVG, no chart lib) ───────────────────────────────
function StackedBarChart({ months }) {
  if (!months || months.length === 0) return null;
  const W = 900;
  const H = 320;
  const padding = { top: 20, right: 40, bottom: 50, left: 50 };
  const innerW = W - padding.left - padding.right;
  const innerH = H - padding.top - padding.bottom;

  const maxTotal = Math.max(...months.map(m => m.total));
  const barWidth = Math.min(60, (innerW / months.length) * 0.7);
  const xStep = innerW / months.length;

  // Y ticks (5 levels)
  const ticks = 5;
  const yScale = (v) => innerH - (v / maxTotal) * innerH;

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16, overflowX: 'auto' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>
        Ranking distribution over time
      </div>
      <div style={{ fontSize: 12, color: C.faint, marginBottom: 16 }}>
        Each bar = total keywords ranking that month, stacked by position bucket
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', minWidth: 600 }}>
        {/* Y-axis grid + labels */}
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const y = padding.top + (innerH * i / ticks);
          const val = Math.round(maxTotal * (1 - i / ticks));
          return (
            <g key={i}>
              <line x1={padding.left} y1={y} x2={padding.left + innerW} y2={y} stroke="#f1f5f9" strokeWidth="1" />
              <text x={padding.left - 8} y={y + 4} fontSize="11" fill={C.faint} textAnchor="end">{val}</text>
            </g>
          );
        })}

        {/* Bars */}
        {months.map((m, i) => {
          const cx = padding.left + xStep * i + xStep / 2;
          const barX = cx - barWidth / 2;
          let yCursor = padding.top + innerH;
          return (
            <g key={m.month}>
              {BUCKETS.map(b => {
                const v = m[b.key] || 0;
                if (v === 0) return null;
                const h = (v / maxTotal) * innerH;
                yCursor -= h;
                return (
                  <rect key={b.key}
                    x={barX} y={yCursor} width={barWidth} height={h}
                    fill={b.color}>
                    <title>{`${b.label}: ${v} keywords (${m.month.slice(0,7)})`}</title>
                  </rect>
                );
              })}
              {/* Total label above bar */}
              <text x={cx} y={padding.top + innerH - (m.total / maxTotal) * innerH - 6}
                fontSize="11" fontWeight="700" fill={C.text} textAnchor="middle">
                {m.total}
              </text>
              {/* X-axis label */}
              <text x={cx} y={padding.top + innerH + 20} fontSize="11" fill={C.muted} textAnchor="middle">
                {fmtMonth(m.month)}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 14, fontSize: 12 }}>
        {BUCKETS.map(b => (
          <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: b.color }} />
            <span style={{ color: C.text, fontWeight: 500 }}>{b.label}</span>
            <span style={{ color: C.faint }}>({b.desc})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Detail table ─────────────────────────────────────────────────────────────
function HistoryTable({ months }) {
  if (!months || months.length === 0) return null;
  // Show newest first in the table
  const reversed = [...months].reverse();

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px 12px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          Monthly detail
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.bg, color: C.muted }}>
              <th style={{ textAlign: 'left',  padding: '10px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Month</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Top 3</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Page 1</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Page 2</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Page 3</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Beyond</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Clicks</th>
              <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Impressions</th>
            </tr>
          </thead>
          <tbody>
            {reversed.map((m, i) => (
              <tr key={m.month} style={{ borderTop: `1px solid ${C.border}` }}>
                <td style={{ padding: '10px 16px', fontWeight: 600, color: C.text }}>{fmtMonth(m.month)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: m.top3 > 0 ? C.good : C.faint, fontWeight: m.top3 > 0 ? 700 : 400 }}>{m.top3}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3b82f6', fontWeight: m.page1 > 0 ? 600 : 400 }}>{m.page1}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: m.page2 > 0 ? C.text : C.faint }}>{m.page2}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: m.page3 > 0 ? C.text : C.faint }}>{m.page3}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: C.faint }}>{m.beyond}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: C.text }}>{m.total}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: m.clicks > 0 ? C.good : C.faint, fontWeight: m.clicks > 0 ? 700 : 400 }}>{(m.clicks || 0).toLocaleString()}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: C.muted }}>{(m.impressions || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main RankingHistory component ────────────────────────────────────────────
function RankingHistory({ websiteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(16);

  const load = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      const r = await fetch(`/keyword-intelligence/api/v1/keywords/history?website_id=${websiteId}&months=${months}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await r.json();
      setData(json);
    } catch (e) {
      console.error('History load error:', e);
    } finally {
      setLoading(false);
    }
  }, [websiteId, months]);

  useEffect(() => { load(); }, [load]);

  if (!websiteId) return (
    <div className="empty-state">
      <div className="empty-icon">🌐</div>
      <h3>No Website Selected</h3>
      <p>Please select a website to view ranking history.</p>
    </div>
  );

  if (loading) return (
    <div className="loading">
      <div className="spinner"></div>
      <p>Loading history...</p>
    </div>
  );

  const monthsArr = data?.months || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>📈 Ranking History</h2>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            Month-over-month ranking distribution — from Google Search Console
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', padding: 4, borderRadius: 10 }}>
          {[6, 12, 16].map(m => (
            <button key={m} onClick={() => setMonths(m)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none',
                background: months === m ? '#fff' : 'transparent',
                color: months === m ? C.text : C.muted,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                boxShadow: months === m ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              {m} months
            </button>
          ))}
        </div>
      </div>

      {monthsArr.length === 0 ? (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <h3 style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 8 }}>No history yet</h3>
          <p style={{ fontSize: 13, color: C.muted, maxWidth: 420, margin: '0 auto' }}>
            We need historical Google Search Console data to show ranking trends.
            Run a backfill from Settings to pull up to 16 months of history.
          </p>
        </div>
      ) : (
        <>
          <HistoryHero months={monthsArr} />
          <StackedBarChart months={monthsArr} />
          <HistoryTable months={monthsArr} />
        </>
      )}
    </div>
  );
}

export default RankingHistory;
